/** Admin API hook'lari. Kalitlar ["user"|"branch"|"settings"|"audit", ...]: real-time hodisalar shularni yangilaydi. */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getAllPages, useIdempotencyKeys, type Paginated, type RequestOptions } from "@/lib/api";
import type {
  AdminUser,
  AppSettingsFull,
  AuditEntry,
  BlockResult,
  Branch,
  BranchWrite,
  SettingsPatch,
  UserAccess,
  UserWrite,
} from "./types";
import type { Permission } from "@/features/auth/types";

type Query = RequestOptions["query"];

export const adminKeys = {
  users: ["user"] as const,
  userList: (q: Query) => ["user", "list", q] as const,
  branches: ["branch"] as const,
  branchList: (q: Query) => ["branch", "list", q] as const,
  settings: ["settings"] as const,
  settingsFull: ["settings", "full"] as const,
  audit: ["audit"] as const,
  auditList: (q: Query) => ["audit", "list", q] as const,
};

// ---- Users ----

/** Ruxsatlar (faqat owner). Kalit "user" ostida: boshqa oynadagi o'zgarish (user hodisasi) buni ham yangilaydi */
export function useUserAccess(id: number | null) {
  return useQuery({
    queryKey: ["user", "access", id],
    queryFn: () => api.get<UserAccess>(`/api/users/${id}/access/`),
    enabled: id !== null,
  });
}

export function useSaveUserAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values, version }: { id: number; values: Partial<Record<Permission, boolean>>; version: number }) =>
      api.patch<UserAccess>(`/api/users/${id}/access/`, { values, version }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users });
      qc.invalidateQueries({ queryKey: ["user", "profile"] });
    },
  });
}

export function useUsers(query: Query) {
  return useQuery({
    queryKey: adminKeys.userList(query),
    queryFn: () => api.get<Paginated<AdminUser>>("/api/users/", { query }),
    placeholderData: keepPreviousData,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  const keys = useIdempotencyKeys();
  return useMutation({
    mutationFn: (data: UserWrite) => api.post<AdminUser>("/api/users/", data, { idempotencyKey: keys.for(data) }),
    onSuccess: (_result, data) => {
      keys.done(data);
      qc.invalidateQueries({ queryKey: adminKeys.users });
      qc.invalidateQueries({ queryKey: adminKeys.branches });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<UserWrite> & { id: number; version: number }) => api.patch<AdminUser>(`/api/users/${id}/`, data),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

export function useBlockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mode, reassign_to }: { id: number; mode: "one" | "pool"; reassign_to?: number | null }) =>
      api.post<BlockResult>(`/api/users/${id}/block/`, { mode, reassign_to: reassign_to ?? null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users });
      qc.invalidateQueries({ queryKey: ["driver"] });
    },
  });
}

export function useUnblockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<AdminUser>(`/api/users/${id}/unblock/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.users }),
  });
}

export function useReset2fa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<AdminUser>(`/api/users/${id}/reset_2fa/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.users }),
  });
}

/** Yangi (vaqtinchalik) parol berish: email yo'q, parolni unutgan xodimga owner/menejer beradi */
export function useSetUserPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) => api.post<{ ok: boolean }>(`/api/users/${id}/set_password/`, { password }),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.users }),
  });
}

// ---- Branches ----

export function useBranches(query: Query = {}) {
  return useQuery({
    queryKey: adminKeys.branchList(query),
    queryFn: () => getAllPages<Branch>("/api/branches/", { ordering: "name", ...query }),
    placeholderData: keepPreviousData,
  });
}

export function useCreateBranch() {
  const qc = useQueryClient();
  const keys = useIdempotencyKeys();
  return useMutation({
    mutationFn: (data: BranchWrite) => api.post<Branch>("/api/branches/", data, { idempotencyKey: keys.for(data) }),
    onSuccess: (_result, data) => {
      keys.done(data);
      qc.invalidateQueries({ queryKey: adminKeys.branches });
    },
  });
}

export function useUpdateBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<BranchWrite> & { id: number; version: number }) => api.patch<Branch>(`/api/branches/${id}/`, data),
    onSettled: () => qc.invalidateQueries({ queryKey: adminKeys.branches }),
  });
}

export function useArchiveBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/branches/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.branches }),
  });
}

// ---- Settings ----

export function useSettings(enabled = true) {
  return useQuery({
    queryKey: adminKeys.settingsFull,
    queryFn: () => api.get<AppSettingsFull>("/api/settings/"),
    enabled,
    staleTime: 60_000,
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SettingsPatch) => api.patch<AppSettingsFull>("/api/settings/", data),
    onSuccess: (data) => {
      qc.setQueryData(adminKeys.settingsFull, data);
      qc.invalidateQueries({ queryKey: adminKeys.settings });
    },
    onSettled: (_data, error) => {
      if (error) qc.invalidateQueries({ queryKey: adminKeys.settings });
    },
  });
}

// ---- Audit log ----

export function useAuditLog(query: Query) {
  return useQuery({
    queryKey: adminKeys.auditList(query),
    queryFn: () => api.get<Paginated<AuditEntry>>("/api/audit/", { query }),
    placeholderData: keepPreviousData,
  });
}
